import { Box, Spinner } from '@aicon-dev/caas-core';
import { Avatar, Button, Input } from '@aicon-dev/caas-ui';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form } from '~/components/ui/Form';
import { InputVirtualNumber } from '~/components/ui/Input/InputVirtualNumber';
import { Upload } from '~/components/ui/Upload';

import { IconDelete } from '../libs/icons';
import { UserFormValues } from '../libs/types';

import { UserFormSection } from './UserFormSection';

interface UserFormInfoProps {
  isSnsUser?: boolean;
}

export const UserFormInfo = (props: UserFormInfoProps) => {
  const { isSnsUser } = props;
  const { t } = useTranslation('user_info');
  const { control, watch } = useFormContext<UserFormValues>();

  const name = watch('name');

  return (
    <UserFormSection>
      <Form.ItemControlled
        control={control}
        name="thumbnailUrl"
        render={({ field: { value, onChange } }) => {
          return (
            <>
              <Upload
                fileList={[]}
                onlyImage
                onChange={(fileList) => {
                  onChange(fileList?.[0]?.url);
                }}
                render={({ trigger, isLoading }) => {
                  return (
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Box
                        position="relative"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="92px"
                        height="92px"
                        borderRadius="100%"
                        border="1px solid #f3f3f3"
                      >
                        {isLoading ? (
                          <Spinner />
                        ) : (
                          <Avatar
                            src={value ?? undefined}
                            name={name}
                            size={90}
                            style={{
                              fontWeight: 700,
                            }}
                          />
                        )}
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="center"
                        pt="12px"
                        style={{
                          gap: 8,
                        }}
                      >
                        {!!value && (
                          <Button color="danger" onClick={() => onChange(null)}>
                            <IconDelete />
                            {t('user_info:delete')}
                          </Button>
                        )}
                        {trigger}
                      </Box>
                    </Box>
                  );
                }}
              >
                <Button>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M17.3334 14.2121C17.3334 14.5657 17.1929 14.9048 16.9429 15.1549C16.6928 15.4049 16.3537 15.5454 16.0001 15.5454H3.99997C3.64634 15.5454 3.3072 15.4049 3.05715 15.1549C2.8071 14.9048 2.66663 14.5657 2.66663 14.2121V6.87874C2.66663 6.52512 2.8071 6.18598 3.05715 5.93593C3.3072 5.68589 3.64634 5.54541 3.99997 5.54541H6.55962C6.62649 5.54541 6.68893 5.51199 6.72603 5.45635L7.94062 3.63447C7.97772 3.57883 8.04016 3.54541 8.10703 3.54541H11.893C11.9599 3.54541 12.0223 3.57883 12.0594 3.63447L13.274 5.45635C13.3111 5.51199 13.3735 5.54541 13.4404 5.54541H16.0001C16.3537 5.54541 16.6928 5.68589 16.9429 5.93593C17.1929 6.18598 17.3334 6.52512 17.3334 6.87874V14.2121Z"
                      stroke="#404040"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.99994 12.8787C11.4727 12.8787 12.6666 11.6848 12.6666 10.2121C12.6666 8.73932 11.4727 7.54541 9.99994 7.54541C8.52717 7.54541 7.33325 8.73932 7.33325 10.2121C7.33325 11.6848 8.52717 12.8787 9.99994 12.8787Z"
                      stroke="#404040"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {t('user_info:change')}
                </Button>
              </Upload>
            </>
          );
        }}
      />

      {/* 이름 */}
      <Form.ItemControlled
        control={control}
        name="name"
        label={t('user_info:name')}
        rules={{ required: t('user_info:name_rule_required').toString() }}
        render={({ field: { value, onChange } }) => {
          return <Input block size="large" value={value} onChange={onChange} />;
        }}
      />

      {/* 전화번호 */}
      <Form.ItemControlled
        control={control}
        name="phoneNumber"
        label={t('user_info:phone_number')}
        rules={{ required: t('user_info:phone_number_rule_required').toString() }}
        render={({ field: { value, onChange } }) => {
          return <InputVirtualNumber block value={value} onChange={onChange} />;
        }}
      />

      {/* 이메일 */}
      <Form.ItemControlled
        control={control}
        name="email"
        label={t('user_info:email')}
        render={({ field: { value, onChange } }) => {
          return <InputVirtualNumber block disabled value={value} onChange={onChange} />;
        }}
      />

      {!isSnsUser && (
        <>
          {/* 새 비밀번호 */}
          <Form.ItemControlled
            control={control}
            name="password"
            label={t('user_info:new_password')}
            rules={{
              required: t('user_info:new_password_rule_required').toString(),
              minLength: {
                value: 8,
                message: t('user_info:password_rule_length').toString(),
              },
              maxLength: {
                value: 15,
                message: t('user_info:password_rule_length').toString(),
              },
            }}
            render={({ field: { value, onChange } }) => {
              return (
                <Input
                  block
                  size="large"
                  type="password"
                  autoComplete="new-password"
                  value={value}
                  onChange={onChange}
                  placeholder={t('user_info:new_password_rule_required').toString()}
                />
              );
            }}
          />

          {/* 비밀번호 확인 */}
          <Form.ItemControlled
            control={control}
            name="checkPassword"
            label={t('user_info:password_check')}
            rules={{
              required: t('user_info:password_check_rule_required').toString(),
              /**
               * 입력한 비밀번호와 값이 동일한지 체크
               */
              validate: (value, formValue) => {
                if (value !== formValue.password) {
                  return t('user_info:password_check_rule_match').toString();
                }
                return true;
              },
            }}
            render={({ field: { value, onChange } }) => {
              return (
                <Input
                  block
                  size="large"
                  type="password"
                  autoComplete="new-password"
                  value={value}
                  onChange={onChange}
                  placeholder={t('user_info:password_check_rule_required').toString()}
                />
              );
            }}
          />
        </>
      )}
    </UserFormSection>
  );
};
